import { useMutation, useQuery } from '@tanstack/react-query';

import { OnboardingAccountView } from '@ndvr/customer-api';

import { FidelityDownloadButton, FidelityDownloadFeedback } from '@/components/fidelity/FidelityApplicationComponents';
import { Button } from '@/components/ui/button';
import { CustomerApi } from 'API';
import { FetcherError } from 'utils/fetcher';

import { OnboardingHousehold } from './useOnboardingHousehold';

export const useTransferAction = (household: Pick<OnboardingHousehold, 'accounts'>, enabled: boolean) => {
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);
    const applications = useQuery({
        queryKey: ['onboarding', 'fidelity-transfer-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getTransferApplications({ accountGUIDs }))[0],
        enabled: enabled && accountGUIDs.length > 0,
    });
    const action = (guid: string) => {
        if (!enabled || !applications.isSuccess) return null;
        const exclusion = applications.data?.excludedAccounts.find((notice) => notice.accountGUID === guid);
        if (exclusion)
            return <span className="text-cocoa-600 max-w-sm text-sm">Transfer unavailable: {exclusion.reason}</span>;
        if (!applications.data?.accountGUIDs.includes(guid)) return null;
        const account = household.accounts.find((row) => row.account.accountGUID === guid)?.account;
        return account ? <TransferRow key={JSON.stringify(account)} account={account} /> : null;
    };
    return { applications, action };
};

export const FidelityTransferStatus = ({
    applications,
}: {
    applications: ReturnType<typeof useTransferAction>['applications'];
}) => {
    if (applications.isPending) return <p role="status">Loading transfers…</p>;
    if (!applications.isError) return null;
    return (
        <div role="alert" className="flex flex-wrap items-center gap-3">
            <p>
                {applications.error instanceof FetcherError ? applications.error.message : 'Could not load transfers.'}
            </p>
            <Button variant="outline" onClick={() => void applications.refetch()}>
                Retry transfers
            </Button>
        </div>
    );
};

const TransferRow = ({ account }: { account: OnboardingAccountView }) => {
    const generate = useMutation({
        mutationFn: () => CustomerApi({ hideError: true }).getTransferForm({ accountGUID: account.accountGUID }),
    });
    return (
        <div className="flex flex-col items-start gap-2">
            <FidelityDownloadButton
                download={generate}
                variant="outline"
                className="shrink-0"
                onClick={() => generate.mutate()}
            >
                Generate transfer
            </FidelityDownloadButton>
            <FidelityDownloadFeedback download={generate} />
        </div>
    );
};
